import { fetchJson } from '@backoffice-frontend/common'
import { Environment, getEnvironment } from '@backoffice-frontend/environment'
import {
  MoiaCard as Card,
  MoiaCardContent as CardContent,
  MoiaCardHeader as CardHeader,
  MoiaButton,
  MoiaGrid,
  MoiaIconLabel,
  SwitchEnvironmentDropdown,
} from '@backoffice-frontend/patterns'
import { Restricted } from '@backoffice-frontend/restricted'
import { BoumRight as Rights } from '@moia-dev/moia-token-claims'
import { CheckmarkIcon } from '@moia-dev/pace-icons'
import { Switch } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Features } from './Features'
import { useFeatureToggles } from './useFeatureToggles'
import { useIsFeatureToggleEnabled } from './useIsFeatureToggleEnabled'

const getFeatureTranslation = (feature: Features) => {
  switch (feature) {
    case Features.FEATURE_TOGGLE_TEST:
      return 'Feature to test Feature Toggles'
    case Features.MUNICH_DEMO:
      return 'All features developed for the Munich demo'
    case Features.DSBOSS_SCENARIOS_TABLE_PACE:
      return 'DSBOSS scenarios table pace'
    case Features.EMERGENCY_DISABLE_SERVICE:
      return 'Emergency disable service'
    case Features.MAPS_ROAD_NETWORK_AREA:
      return 'New Road Network Area'
    case Features.SERVICE_BREAK_LOCATIONS:
      return 'New Service Break Locations Area'
    case Features.AD_HARDWARE_COMPONENTS_ATTRIBUTES:
      return 'AD Hardware Attributes'
    default:
      return feature
  }
}

export const FeatureToggle = ({
  children,
  feature,
  replacement,
}: {
  children: ReactNode
  feature: Features
  replacement?: ReactNode
}) => {
  const isEnabled = useIsFeatureToggleEnabled(feature)
  return <>{isEnabled ? children : (replacement ?? null)}</>
}

export const FeatureTogglesView = () => {
  const { features: enabledFeatures, setFeatures } = useFeatureToggles()
  const [throwError, setThrowError] = useState(false)

  const handleCreateError = () => {
    setThrowError(true)
  }

  const handleToggleFeature = (name: Features) => {
    const newFeatures = enabledFeatures.includes(name)
      ? enabledFeatures.filter(feature => feature !== name)
      : [...enabledFeatures, name]

    setFeatures(newFeatures)
  }

  if (throwError) {
    throw new Error(
      'Account area error, intentionally thrown for testing purposes.',
    )
  }

  return (
    <Restricted requiredRights={[Rights.DEV_USE_FEATURE_TOGGLES]}>
      <Card>
        <CardHeader title="Environment" />
        <CardContent>
          <SwitchEnvironmentDropdown />
        </CardContent>

        <CardHeader title="Enable feature" />
        <CardContent>
          <FeatureToggle feature={Features.FEATURE_TOGGLE_TEST}>
            <MoiaIconLabel>
              <CheckmarkIcon />
              Test feature toggle works
            </MoiaIconLabel>
          </FeatureToggle>

          {Object.values(Features).map(feature => (
            <div
              key={feature}
              css={{
                display: 'grid',
                gap: 8,
                gridTemplateColumns: 'min-content auto',
                marginBottom: 4,
                alignItems: 'center',
              }}
            >
              <Switch
                id={`feature-switch-${feature}`}
                onChange={() => handleToggleFeature(feature)}
                checked={enabledFeatures.includes(feature)}
              />
              <label htmlFor={`feature-switch-${feature}`}>
                {getFeatureTranslation(feature)}
              </label>
            </div>
          ))}
          <MoiaGrid column>
            <MoiaButton onClick={handleCreateError}>Throw an Error!</MoiaButton>
            {getEnvironment() === Environment.local && (
              <MoiaButton
                onClick={() => {
                  void fetchJson('http://localhost:8081/api/reset', {
                    method: 'PATCH',
                  })
                }}
              >
                Reset Mock Server
              </MoiaButton>
            )}
          </MoiaGrid>
        </CardContent>
      </Card>
    </Restricted>
  )
}
